import { Button, ButtonGroup, Card, CardBody, Flex, Icon, Progress, Text } from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import DatePicker from 'react-date-picker';
import { BiCalendar, BiPlus, BiX } from 'react-icons/bi';
import { Link } from 'react-router-dom';

import { CopiableGuid } from '@/components/CopiableGuid';
import { getAllocationTypeLabel } from 'utils';
import { AIRFLOW_GUID } from 'utils/service-identities';

import { AllocationTypesInner, OptimizationMeta } from '../_openapi/portfolio-optimizer';
import { useOptimizations } from '../api/optimization';
import { DEFAULT_PAGE_SIZE } from '../constants';
import {
    duration,
    formatDate,
    parseDate,
    SERVER_DATE_FORMAT,
    today,
    UI_DATE_FORMAT,
    yesterday,
} from '../utils/dateUtils';
import { PageContent } from '../widgets/PageContent';
import { PageHeader } from '../widgets/PageHeader';
import { baseCol, dateCol, dateTimeCol, expanderCol, textCol } from '../widgets/Table/columnHelpers/colDef';
import { ControlToggle } from '../widgets/Table/ControlToggle';
import { Pagination } from '../widgets/Table/Pagination';
import { Table } from '../widgets/Table/Table';

interface OptimizationRowDetailProps {
    optimization: OptimizationMeta;
}

const OptimizationRowDetail = ({ optimization }: OptimizationRowDetailProps) => {
    if (!optimization) {
        return undefined;
    }
    const { optimizationRequest } = optimization;
    return (
        <div className="rounded-lg border border-gray-700/50 bg-gray-800/30 p-4">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Allocation Types */}
                <div className="flex flex-col space-y-1">
                    <span className="text-xs tracking-wider text-gray-400 uppercase">Allocation Types</span>
                    <div>
                        {optimizationRequest?.allocationTypes?.length ? (
                            <div className="flex flex-wrap gap-2">
                                {optimizationRequest.allocationTypes.map(
                                    (allocationType: AllocationTypesInner, index: number) => (
                                        <div
                                            key={index}
                                            className="rounded border border-gray-600/30 bg-gray-700/30 px-2 py-1 text-xs text-gray-300"
                                        >
                                            {getAllocationTypeLabel(allocationType as string)}
                                        </div>
                                    ),
                                )}
                            </div>
                        ) : (
                            <span className="font-medium text-gray-200">-</span>
                        )}
                    </div>
                </div>

                {/* Account List */}
                <div className="flex flex-col space-y-1">
                    <span className="text-xs tracking-wider text-gray-400 uppercase">
                        Accounts ({optimizationRequest?.accountGuids?.length || 0})
                    </span>
                    <span className="font-mono text-sm font-medium text-gray-200">
                        {optimizationRequest?.accountGuids?.length ? (
                            <>
                                {optimizationRequest.accountGuids.slice(0, 8).join(', ')}
                                {optimizationRequest.accountGuids.length > 8 && (
                                    <span className="font-sans text-gray-400">
                                        {' '}
                                        & {optimizationRequest.accountGuids.length - 8} more
                                    </span>
                                )}
                            </>
                        ) : (
                            '-'
                        )}
                    </span>
                </div>
            </div>
        </div>
    );
};

export const OptimizationList = () => {
    // Initialize date range with default values: yesterday to today
    const [dateFrom, setDateFrom] = useState<string>(formatDate(today(), SERVER_DATE_FORMAT)!);
    const [dateTo, setDateTo] = useState<string>(formatDate(today(), SERVER_DATE_FORMAT)!);
    const [showCircleOptimizations, setShowCircleOptimizations] = useState(false);

    // Use API-level date filtering (only pass non-empty date values)
    const { data: optimizations = [], isLoading } = useOptimizations(dateFrom || undefined, dateTo || undefined);

    const data = useMemo(() => {
        let list = optimizations;

        if (!showCircleOptimizations) {
            list = list.filter(({ owner }) => owner !== AIRFLOW_GUID);
        }

        return list;
    }, [optimizations, showCircleOptimizations]);

    const table = useReactTable({
        defaultColumn: {
            enableColumnFilter: false,
            enableSorting: false,
        },
        initialState: {
            pagination: {
                pageSize: DEFAULT_PAGE_SIZE,
            },
        },
        data,
        columns,
        getRowCanExpand: () => true,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        sortDescFirst: false,
    });

    return (
        <>
            <PageHeader title="Optimization results">
                <Flex gap={4} alignItems="center">
                    <Flex alignItems="center" gap={2}>
                        <Text fontSize="sm" color="gray.400">
                            From
                        </Text>
                        <div className="w-44">
                            <DatePicker
                                format={UI_DATE_FORMAT}
                                onChange={(value) =>
                                    setDateFrom(value ? formatDate(value as Date, SERVER_DATE_FORMAT)! : '')
                                }
                                value={dateFrom}
                                clearIcon={<Icon as={BiX} />}
                                calendarIcon={<Icon as={BiCalendar} />}
                                shouldCloseCalendar={() => true}
                            />
                        </div>
                        <Text fontSize="sm" color="gray.400">
                            To
                        </Text>
                        <div className="w-44">
                            <DatePicker
                                format={UI_DATE_FORMAT}
                                onChange={(value) =>
                                    setDateTo(value ? formatDate(value as Date, SERVER_DATE_FORMAT)! : '')
                                }
                                value={dateTo}
                                clearIcon={<Icon as={BiX} />}
                                calendarIcon={<Icon as={BiCalendar} />}
                                shouldCloseCalendar={() => true}
                            />
                        </div>
                    </Flex>
                    <ControlToggle
                        label="Show Circle CI optimizations"
                        isChecked={showCircleOptimizations}
                        onChange={setShowCircleOptimizations}
                    />
                    <Button as={Link} to="builder" colorScheme="teal" size="sm" leftIcon={<Icon as={BiPlus} />}>
                        Create new optimization
                    </Button>
                </Flex>
            </PageHeader>
            <PageContent isLoading={isLoading} loadingText="Loading optimizations">
                <Card>
                    <CardBody>
                        {data.length ? (
                            <>
                                <Table
                                    table={table}
                                    SubComponent={(row) => <OptimizationRowDetail optimization={row.row.original} />}
                                />
                                <Pagination table={table} />
                            </>
                        ) : (
                            <Text>
                                <i>No optimizations found for the selected date range</i>
                            </Text>
                        )}
                    </CardBody>
                </Card>
            </PageContent>
        </>
    );
};

const columnHelper = createColumnHelper<OptimizationMeta>();
const columns = [
    columnHelper.display(expanderCol()),
    columnHelper.accessor('optimizationId', textCol({ id: 'optimizationId' })),
    columnHelper.accessor('tradeDate', dateCol({ id: 'tradeDate' })),
    columnHelper.accessor('owner', {
        ...baseCol({ id: 'owner' }),
        cell: ({ getValue }) => <CopiableGuid className="text-white" guid={getValue()} showBorder={false} />,
    }),
    columnHelper.accessor((row) => row.optimizationRequest?.algoAction, {
        ...textCol({ id: 'algoAction', header: 'Action' }),
        cell: ({ getValue }) => {
            const action = getValue();
            return (
                <Text fontSize="sm" fontWeight="medium" color={action === 'sell' ? 'red.200' : 'green.200'}>
                    {action?.toUpperCase() || 'N/A'}
                </Text>
            );
        },
    }),
    columnHelper.accessor('details', textCol({ id: 'details' })),
    columnHelper.accessor('startTimeEst', dateTimeCol({ id: 'startTimeEst' })),
    columnHelper.accessor('totalSeconds', {
        ...baseCol({ id: 'totalSeconds' }),
        cell: ({ row }) => {
            if (!row.original.completionTimeEst) {
                return (
                    <Progress
                        maxWidth="120px"
                        colorScheme="teal"
                        isIndeterminate
                        mt={1}
                        sx={{ '& > div': { animationDuration: '2s' } }}
                    />
                );
            }
            return (
                <Text fontSize="sm" whiteSpace="nowrap">
                    {duration(parseDate(row.original.startTimeEst)!, parseDate(row.original.completionTimeEst)!)}
                </Text>
            );
        },
    }),
    columnHelper.display({
        id: 'actions',
        cell: ({ row }) => {
            const resultsDisabled = !row.original.completionTimeEst;
            return (
                <ButtonGroup>
                    <Button
                        as={Link}
                        to={resultsDisabled ? '#' : row.original.optimizationId.toString()}
                        size="xs"
                        colorScheme="teal"
                        variant="outline"
                        isDisabled={resultsDisabled}
                    >
                        Check results
                    </Button>
                    <Button
                        as={Link}
                        to={`builder?rerun=${row.original.optimizationId.toString()}`}
                        size="xs"
                        variant="outline"
                        isDisabled={resultsDisabled}
                    >
                        Rerun
                    </Button>
                </ButtonGroup>
            );
        },
    }),
];
